import React, { useCallback, useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import Langs, { Localize } from '../../app/lang/langs';

/**
 * Square-crop editor for a collab's profile picture.
 *
 * The browser does the cropping and the downscaling, and ships the finished 256x256 square.
 * That is deliberate: the backend has no image library, so it validates the result
 * (square, <=256px, allowed format, size cap) rather than transforming anything. Whatever
 * this component emits is what gets stored.
 *
 * Interaction: the source image is scaled to *cover* a square viewport, then dragged to
 * choose which part of it lands inside. Zoom scales up from that cover baseline, so the
 * square can never contain empty space — which is what guarantees a full-bleed 256x256
 * output with no letterboxing, whatever the shape of the file the user picked.
 */

// Side of the on-screen crop viewport. Independent of the output size — this is just how
// big the control is; OUTPUT_SIZE is what gets saved.
const VIEWPORT_SIZE = 288;

// Must match MAX_SIDE in the backend's CollabPhotoController. The upload is rejected above
// this, so the two constants are a contract, not a coincidence.
const OUTPUT_SIZE = 256;

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;

// Rejected before decoding rather than after: a 40MP phone photo would otherwise be fully
// decoded into memory just to be refused. The output is 256px, so nothing this large is
// needed as input.
const MAX_SOURCE_BYTES = 20 * 1024 * 1024;

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/bmp'];

/**
 * Encode the cropped canvas.
 *
 * WebP first — for a face it is meaningfully smaller than JPEG at the same quality, which
 * matters because these bytes sit in a database row. Safari could not encode WebP from a
 * canvas until 16, and `toBlob` there hands back a PNG while happily reporting success, so
 * the result's real type is checked rather than assumed and JPEG is used when it differs.
 */
const encodeCanvas = (canvas) => new Promise((resolve) => {
    canvas.toBlob((webpBlob) => {
        if (webpBlob && webpBlob.type === 'image/webp') {
            resolve(webpBlob);
            return;
        }
        canvas.toBlob((jpegBlob) => resolve(jpegBlob), 'image/jpeg', 0.9);
    }, 'image/webp', 0.9);
});

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
});

export function ProfilePictureEditor({ open, onClose, onSave, saving = false, error = null, title = null }) {

    const [imageElement, setImageElement] = useState(null);
    const [objectUrl, setObjectUrl] = useState(null);
    const [zoom, setZoom] = useState(MIN_ZOOM);
    // Top-left of the scaled image relative to the viewport's top-left. Always <= 0.
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [localError, setLocalError] = useState(null);
    const [encoding, setEncoding] = useState(false);

    const fileInputRef = useRef(null);
    const dragState = useRef(null);

    // Scale at which the image exactly covers the viewport on its shorter side.
    const baseScale = imageElement
        ? VIEWPORT_SIZE / Math.min(imageElement.naturalWidth, imageElement.naturalHeight)
        : 1;
    const scale = baseScale * zoom;
    const displayWidth = imageElement ? imageElement.naturalWidth * scale : 0;
    const displayHeight = imageElement ? imageElement.naturalHeight * scale : 0;

    // Keeps the viewport fully covered: the image may not be dragged past its own edges.
    const clampOffset = useCallback((next, width, height) => ({
        x: Math.min(0, Math.max(VIEWPORT_SIZE - width, next.x)),
        y: Math.min(0, Math.max(VIEWPORT_SIZE - height, next.y)),
    }), []);

    // Zooming out shrinks the image, which can leave the old offset pointing outside it —
    // so the offset is re-clamped whenever the scale changes, not only while dragging.
    useEffect(() => {
        if (!imageElement) return;
        setOffset((current) => clampOffset(current, displayWidth, displayHeight));
    }, [imageElement, displayWidth, displayHeight, clampOffset]);

    // Object URLs are a document-lifetime leak until revoked; the dialog can be opened and
    // closed repeatedly with a different file each time.
    useEffect(() => () => {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
    }, [objectUrl]);

    const resetState = () => {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        setObjectUrl(null);
        setImageElement(null);
        setZoom(MIN_ZOOM);
        setOffset({ x: 0, y: 0 });
        setLocalError(null);
        setEncoding(false);
    };

    const handleClose = () => {
        resetState();
        onClose();
    };

    const handleFileSelected = (file) => {
        if (!file) return;
        setLocalError(null);

        if (!ACCEPTED_TYPES.includes(file.type)) {
            setLocalError(Localize('PHOTO_ERROR_TYPE'));
            return;
        }
        if (file.size > MAX_SOURCE_BYTES) {
            setLocalError(Localize('PHOTO_ERROR_SOURCE_TOO_LARGE'));
            return;
        }

        const url = URL.createObjectURL(file);
        const image = new Image();
        image.onload = () => {
            // A source smaller than the output would be upscaled into a blurry square.
            if (Math.min(image.naturalWidth, image.naturalHeight) < 64) {
                URL.revokeObjectURL(url);
                setLocalError(Localize('PHOTO_ERROR_TOO_SMALL'));
                return;
            }
            if (objectUrl) URL.revokeObjectURL(objectUrl);
            setObjectUrl(url);
            setImageElement(image);
            setZoom(MIN_ZOOM);
            // Centre the image in the viewport rather than pinning it top-left: for a wide
            // photo the middle is nearly always the wanted crop.
            const initialScale = VIEWPORT_SIZE / Math.min(image.naturalWidth, image.naturalHeight);
            setOffset({
                x: (VIEWPORT_SIZE - image.naturalWidth * initialScale) / 2,
                y: (VIEWPORT_SIZE - image.naturalHeight * initialScale) / 2,
            });
        };
        image.onerror = () => {
            URL.revokeObjectURL(url);
            setLocalError(Localize('PHOTO_ERROR_UNREADABLE'));
        };
        image.src = url;
    };

    // Pointer events rather than mouse+touch pairs: one code path covers mouse, touch and
    // stylus, and setPointerCapture keeps the drag alive when the cursor leaves the box.
    const handlePointerDown = (event) => {
        if (!imageElement) return;
        event.currentTarget.setPointerCapture(event.pointerId);
        dragState.current = {
            startX: event.clientX,
            startY: event.clientY,
            originX: offset.x,
            originY: offset.y,
        };
    };

    const handlePointerMove = (event) => {
        if (!dragState.current) return;
        const { startX, startY, originX, originY } = dragState.current;
        setOffset(clampOffset(
            { x: originX + (event.clientX - startX), y: originY + (event.clientY - startY) },
            displayWidth,
            displayHeight,
        ));
    };

    const handlePointerUp = (event) => {
        if (!dragState.current) return;
        event.currentTarget.releasePointerCapture(event.pointerId);
        dragState.current = null;
    };

    const handleSave = async () => {
        if (!imageElement) return;
        setEncoding(true);
        setLocalError(null);
        try {
            const canvas = document.createElement('canvas');
            canvas.width = OUTPUT_SIZE;
            canvas.height = OUTPUT_SIZE;
            const context = canvas.getContext('2d');

            // The viewport shows a `scale`d image at `offset`; the source rectangle is that
            // mapping inverted back into the image's own pixel coordinates.
            const sourceX = -offset.x / scale;
            const sourceY = -offset.y / scale;
            const sourceSide = VIEWPORT_SIZE / scale;

            // Smooths the downscale; without it a large photo reduced to 256px aliases badly.
            context.imageSmoothingEnabled = true;
            context.imageSmoothingQuality = 'high';

            // A transparent PNG would otherwise crop to transparent pixels, which render as
            // black in some contexts once re-encoded to JPEG.
            context.fillStyle = '#ffffff';
            context.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);

            context.drawImage(
                imageElement,
                sourceX, sourceY, sourceSide, sourceSide,
                0, 0, OUTPUT_SIZE, OUTPUT_SIZE,
            );

            const blob = await encodeCanvas(canvas);
            if (!blob) {
                setLocalError(Localize('PHOTO_ERROR_ENCODE'));
                setEncoding(false);
                return;
            }

            const dataUrl = await blobToDataUrl(blob);
            setEncoding(false);
            // The parent owns the request and the success/failure feedback; this component
            // only produces the image.
            onSave(dataUrl);
        } catch (encodeError) {
            setEncoding(false);
            setLocalError(Localize('PHOTO_ERROR_ENCODE'));
        }
    };

    const busy = saving || encoding;
    const shownError = error || localError;

    return (
        <Dialog open={open} onClose={busy ? undefined : handleClose} maxWidth="xs" fullWidth>
            {/* `title` is passed when an admin is setting someone else's picture, so the
                dialog says whose. Own-picture use passes nothing and keeps the generic
                heading. */}
            <DialogTitle>{title || <Langs str="PHOTO_EDIT_TITLE" />}</DialogTitle>

            <DialogContent>
                <Stack spacing={2} alignItems="center">

                    {shownError ? (
                        <Alert severity="error" sx={{ width: '100%' }}>{shownError}</Alert>
                    ) : null}

                    <Box
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUp}
                        onPointerCancel={handlePointerUp}
                        sx={{
                            width: VIEWPORT_SIZE,
                            height: VIEWPORT_SIZE,
                            maxWidth: '100%',
                            position: 'relative',
                            overflow: 'hidden',
                            // Round, because that is how the avatar is displayed everywhere —
                            // the crop should show what the result will actually look like.
                            borderRadius: '50%',
                            border: '2px dashed',
                            borderColor: imageElement ? 'transparent' : 'divider',
                            backgroundColor: 'action.hover',
                            cursor: imageElement ? 'grab' : 'pointer',
                            // Stops the browser from panning the page instead of the image.
                            touchAction: 'none',
                            userSelect: 'none',
                        }}
                        onClick={imageElement ? undefined : () => fileInputRef.current?.click()}
                    >
                        {imageElement ? (
                            <img
                                src={objectUrl}
                                alt=""
                                draggable={false}
                                style={{
                                    position: 'absolute',
                                    left: offset.x,
                                    top: offset.y,
                                    width: displayWidth,
                                    height: displayHeight,
                                    pointerEvents: 'none',
                                }}
                            />
                        ) : (
                            <Stack
                                justifyContent="center"
                                alignItems="center"
                                sx={{ height: '100%', px: 3, textAlign: 'center' }}
                            >
                                <Typography variant="body2" color="text.secondary">
                                    <Langs str="PHOTO_PICK_PROMPT" />
                                </Typography>
                            </Stack>
                        )}
                    </Box>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPTED_TYPES.join(',')}
                        style={{ display: 'none' }}
                        onChange={(event) => {
                            handleFileSelected(event.target.files?.[0]);
                            // Cleared so picking the same file twice in a row still fires
                            // onChange.
                            event.target.value = '';
                        }}
                    />

                    {imageElement ? (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                            <ZoomOutIcon fontSize="small" color="action" />
                            <Slider
                                value={zoom}
                                min={MIN_ZOOM}
                                max={MAX_ZOOM}
                                step={0.01}
                                disabled={busy}
                                onChange={(_event, value) => setZoom(value)}
                                aria-label={Localize('PHOTO_ZOOM')}
                            />
                            <ZoomInIcon fontSize="small" color="action" />
                        </Stack>
                    ) : null}

                    <Typography variant="caption" color="text.secondary" textAlign="center">
                        <Langs str={imageElement ? "PHOTO_DRAG_HINT" : "PHOTO_FORMATS_HINT"} />
                    </Typography>

                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={busy}>
                    <Langs str="CANCEL" />
                </Button>
                <Button onClick={() => fileInputRef.current?.click()} disabled={busy}>
                    <Langs str={imageElement ? "PHOTO_CHANGE_FILE" : "PHOTO_CHOOSE_FILE"} />
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={busy || !imageElement}
                    startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
                >
                    <Langs str="SAVE" />
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ProfilePictureEditor;
